import {
	isErrorStatusCode,
	localStorageGetBoolean,
	localStorageGetNumber,
} from "@/utils";
import {
	Watcher,
	WatcherRegexResult,
	WatcherResourceResult,
	WatcherResult,
} from "@/utils/watcher";
import {
	getSitemapsFromRobots,
	getUrlsFromSitemap,
	stripSearchFromUrl,
} from "@/utils/watcher/utils";
import { computed, reactive, Ref, ref } from "vue";
import { notificationsService } from "./notificationsService";
import { regexWatcherService } from "./regexWatcherService";
import {
	ResourceResult,
	resourceWatcherService,
} from "./resourceWatcherService";

export type Result = WatcherResult & {
	sitemap: string;
};

export type SitemapStats = {
	running: boolean;
	show: boolean;
	randomize: boolean;
	totalUrls: number | null;
	progress: number;
	count: number;
	statusesCount: Record<number, number>;
};

export const watcherService = (() => {
	const { testResources } = resourceWatcherService;
	const { enabledRegexes, regexResults } = regexWatcherService;

	const sitemapStats: Ref<Record<string, SitemapStats>> = ref({});
	const watchers: Ref<Record<string, Watcher>> = ref({});

	const delayMS = ref(localStorageGetNumber("delayMS", 2000));
	const watchResources = ref(localStorageGetBoolean("watchResources", false));
	const randomize = ref(localStorageGetBoolean("randomize", false));

	const maxRequests = ref(
		Math.max(localStorageGetNumber("maxRequests", 1), 1)
	);
	let pendingRequestsCount = 0;

	const sitemapUrls = computed(() => Object.keys(sitemapStats.value));
	const visibleSitemaps = computed(() => {
		const visible: string[] = [];
		for (const [sitemap, stats] of Object.entries(sitemapStats.value)) {
			if (stats.show) {
				visible.push(sitemap);
			}
		}
		return visible;
	});

	const results = ref<Result[]>([]);
	const filteredResults = computed(() => {
		return results.value.filter(result => {
			return visibleSitemaps.value.includes(result.sitemap);
		});
	});

	const seenStatuses = computed(() => {
		const statuses = new Set<number>();
		for (const stats of Object.values(sitemapStats.value)) {
			for (const status of Object.keys(stats.statusesCount)) {
				statuses.add(parseInt(status));
			}
		}
		return [...statuses];
	});

	const _addStats = (url: string) => {
		if (url in sitemapStats.value) {
			return sitemapStats.value[url];
		}
		const stats: SitemapStats = reactive({
			running: false,
			show: true,
			randomize: false,
			totalUrls: null,
			progress: 0,
			count: 0,
			statusesCount: {},
		});
		sitemapStats.value[url] = stats;
		return stats;
	};

	const _attachWatcher = async (url: string) => {
		const urls = await getUrlsFromSitemap(url);
		const watcher = new Watcher(urls);

		let stats = sitemapStats.value[url];
		if (!stats) {
			stats = _addStats(url);
		}
		stats.totalUrls = urls.length;

		watchers.value[url] = watcher;

		watcher.events.subscribe(
			"regex",
			async (result: WatcherRegexResult) => {
				regexResults.value.push({ ...result, sitemap: url });
			}
		);

		watcher.events.subscribe("result", async ({ result, progress }) => {
			results.value.push({
				...result,
				sitemap: url,
			});
			stats.progress = progress;
			stats.count++;

			// increase the status counter
			const currentCount = stats.statusesCount[result.status];
			stats.statusesCount[result.status] = currentCount
				? currentCount + 1
				: 1;

			const sendErrorNotification = (url: string, status: number) => {
				notificationsService.createNotification("Error Encountered", {
					body: `URL ${url} responded with status code ${status}`,
					requireInteraction: true,
				});
			};

			// show desktop notification on error
			if (isErrorStatusCode(result.status)) {
				sendErrorNotification(result.url, result.status);
			}

			if (watchResources.value) {
				// test resources on the page
				testResources(
					result.resources,
					url,
					result.url,
					(resourceResult: ResourceResult) => {
						// show desktop notification on resource error
						if (isErrorStatusCode(resourceResult.status)) {
							sendErrorNotification(
								resourceResult.url,
								resourceResult.status
							);
						}
					}
				);
			}
		});

		watcher.events.subscribe(
			"resource",
			(result: WatcherResourceResult) => {
				// show desktop notification on error
				if (isErrorStatusCode(result.status)) {
					notificationsService.createNotification(
						"Error Encountered",
						{
							body: `URL ${result.url} responded with status code ${result.status}`,
							requireInteraction: true,
						}
					);
				}
			}
		);

		watcher.events.subscribe("finished", () => {
			sitemapStats.value[url].running = false;
		});
	};

	const addRobotsTxt = async (url: string) => {
		const sitemaps = await getSitemapsFromRobots(url);

		await Promise.all(
			sitemaps.map(sitemap => {
				_addStats(sitemap);
				return _attachWatcher(sitemap);
			})
		);
	};

	const validateSitemap = (url: string) => {
		const urlObj = new URL(url);
		return (
			urlObj.hash === "" &&
			urlObj.search === "" &&
			(urlObj.pathname === "/robots.txt" ||
				urlObj.pathname.endsWith(".xml") ||
				urlObj.pathname === "/")
		);
	};

	const addSitemap = async (url: string) => {
		if (!validateSitemap(url)) {
			throw new Error("Invalid sitemap");
		}

		const urlObj = new URL(url);
		url = stripSearchFromUrl(url);

		if (url in watchers.value) {
			return;
		}

		if (urlObj.pathname === "/robots.txt") {
			await addRobotsTxt(url);
		} else if (url.endsWith(".xml")) {
			_addStats(url);
			await _attachWatcher(url);
		} else if (urlObj.pathname === "/") {
			await addRobotsTxt(url + "robots.txt");
			await _attachWatcher(url + "sitemap.xml");
		}

		saveSitemaps(sitemapUrls.value);
	};

	const removeSitemap = (sitemap: string) => {
		if (sitemap in watchers.value) {
			delete watchers.value[sitemap];
		}

		if (sitemap in sitemapStats.value) {
			delete sitemapStats.value[sitemap];
		}

		saveSitemaps(sitemapUrls.value);
	};

	const continueWatching = (sitemap: string) => {
		const stats = sitemapStats.value[sitemap];
		const isFinished =
			stats.totalUrls === null || stats.count >= stats.totalUrls;
		if (!isFinished && watchers.value[sitemap]) {
			sitemapStats.value[sitemap].running = true;
		}
	};

	const pauseWatching = (sitemap: string) => {
		sitemapStats.value[sitemap].running = false;
	};

	const setDelay = (value: number) => {
		value = Math.max(0, value);
		delayMS.value = value;
		localStorage["delayMS"] = value;
	};

	const setWatchResources = (value: boolean) => {
		watchResources.value = value;
		localStorage["watchResources"] = value;
	};

	const setRandomize = (value: boolean) => {
		randomize.value = value;
		localStorage["randomize"] = value;
	};

	const setSitemapVisibility = (sitemap: string, visible: boolean) => {
		if (sitemapStats.value[sitemap]) {
			sitemapStats.value[sitemap].show = visible;
		}
	};

	const setSitemapRandomize = (sitemap: string, value: boolean) => {
		sitemapStats.value[sitemap].randomize = value;
	};

	const reorderSitemaps = (newOrder: string[]) => {
		// javascript objects preserve the order of insertion so we need to
		// create a new object with properly ordered keys
		const newSitemapStats = {} as Record<string, SitemapStats>;
		for (const sitemap of newOrder) {
			newSitemapStats[sitemap] = sitemapStats.value[sitemap];
		}
		sitemapStats.value = newSitemapStats;
		saveSitemaps(sitemapUrls.value);
	};

	// restore sitemaps from localStorage
	const savedSitemaps = restoreSitemaps();
	for (const sitemap of savedSitemaps) {
		_attachWatcher(sitemap);
		_addStats(sitemap);
	}

	// start the processing of the next url
	const _testNextUrl = async () => {
		if (pendingRequestsCount >= maxRequests.value) {
			return;
		}

		const enabledSitemaps = [];
		for (const [sitemap, stats] of Object.entries(sitemapStats.value)) {
			if (stats.running) {
				enabledSitemaps.push(sitemap);
			}
		}

		if (enabledSitemaps.length !== 0) {
			const index = randomize.value
				? Math.floor(enabledSitemaps.length * Math.random())
				: 0;
			const sitemap = enabledSitemaps[index];
			const watcher = watchers.value[sitemap];
			const stats = sitemapStats.value[sitemap];

			pendingRequestsCount++;
			await watcher.testNextUrl({
				regexes: enabledRegexes.value,
				randomize: stats.randomize,
			});
			pendingRequestsCount--;
		}

		setTimeout(
			_testNextUrl,
			enabledSitemaps.length === 0 ? 100 : delayMS.value
		);
	};
	_testNextUrl();

	const setMaxRequests = (value: number) => {
		// more than 20 parallel requests is probably an overkill
		value = Math.max(1, Math.min(value, 20));
		maxRequests.value = value;
		localStorage["maxRequests"] = value;

		// start new requests if available
		const diff = value - pendingRequestsCount;
		for (let i = 0; i < diff; i++) {
			_testNextUrl();
		}
	};

	return {
		sitemaps: sitemapUrls,
		sitemapStats: sitemapStats,
		watchers,

		results: filteredResults,
		seenStatuses,

		maxRequests,
		setMaxRequests,

		validateSitemap,
		addSitemap,
		removeSitemap,
		reorderSitemaps,

		continueWatching,
		pauseWatching,

		delayMS,
		setDelay,

		watchResources,
		setWatchResources,

		randomize,
		setRandomize,

		setSitemapVisibility,
		setSitemapRandomize,
	};
})();

function saveSitemaps(sitemaps: string[]) {
	localStorage.setItem("sitemaps", JSON.stringify(sitemaps));
}

function restoreSitemaps(): string[] {
	const sitemaps = localStorage.getItem("sitemaps");
	return sitemaps ? JSON.parse(sitemaps) : [];
}
